/**
 * Monthly vs. annual billing-period helpers for subscription plans (ticket: annual plans for
 * Starter/Premium/Professional, backend attribute tracked separately under #2487/#2489).
 *
 * `subscriptionType.billing` already exists (currently 'daily' for the free Professional trial,
 * otherwise implicitly monthly - see the "täglich"/"Monat" price-suffix checks that read it
 * directly) and old, unreachable 2021-era mock data in signup/Signup.js used exactly this field
 * with 'annually'/'monthly' for the same purpose, so that's the field this reads rather than
 * inventing a new one. `subscriptionBilling` is the flattened equivalent on the
 * "currentSubscription"/"nextSubscription" view-model objects built in Main.js/Dashboard.js/
 * YourSubscription.js.
 *
 * ASSUMPTION (documented here because the backend doesn't send an annual/monthly value yet at
 * the time this was written): once #2487 ships, `billing` is expected to also carry 'annual'
 * alongside its existing 'daily' value. Annual plans are renewed and invoiced once a year for
 * the full amount (per the ticket), so `price` on an annual subscription type is taken to be the
 * yearly total, not a monthly figure. Anything that isn't recognized as annual (including
 * 'daily', and everything before #2487 ships) is treated as monthly, so nothing about the
 * existing plans changes until the backend value is actually present.
 *
 * If the real value ends up shaped differently, this file is the only place that needs to
 * change - every screen that shows plan pricing (NewSubscription, PaymentConfirmationModal)
 * goes through these helpers rather than reading `billing`/`price` directly.
 *
 * Partner plans (#2357, e.g. Haus & Grund) split the two meanings `billing` had so far:
 * `termPeriod` ('annual' / 'monthly') is the plan's term, and `billing` is 'prepaid' because the
 * customer paid the partner's shop up front, so hellohousing never charges the plan itself. The
 * backend sets `termPeriod` on the existing plans too (from their `billing`), so it's read first,
 * with `billing` as the fallback for data from before it existed.
 */
import moment from 'moment';
import { formatNumber } from './stringsManipulations';
import { SUBSCRIPTION_CATEGORY, getSubscriptionTier, getSubscriptionTierRank, isProfessionalTier } from './subscriptionFeatureAccess';
import { getSubscriptionDisplayName, getSubscriptionPartner, isPartnerSubscription } from './partners';

export const BILLING_PERIOD = {
  MONTHLY: 'MONTHLY',
  ANNUAL: 'ANNUAL',
};

export const getBillingPeriod = (subscriptionType) =>
  (subscriptionType?.termPeriod || subscriptionType?.billing || subscriptionType?.subscriptionBilling)?.toString().toUpperCase().startsWith('ANNUAL')
    ? BILLING_PERIOD.ANNUAL
    : BILLING_PERIOD.MONTHLY;

// Paid up front in a partner's shop (#2357): hellohousing doesn't renew, invoice or cancel it.
// Every partner plan counts, whatever its `billing` says - staging sends the Haus & Grund plans as
// "annual" - and so does a plan marked `billing: 'prepaid'`.
export const isPrepaidSubscription = (subscription) =>
  (subscription?.billing || subscription?.subscriptionBilling)?.toString().toLowerCase() === 'prepaid' ||
  isPartnerSubscription(subscription);

// "1 Jahr" / "1 Monat", the term a partner code buys.
export const getTermLabel = (subscription) =>
  getBillingPeriod(subscription) === BILLING_PERIOD.ANNUAL ? '1 Jahr' : '1 Monat';

// The API sends .NET's DateTime.MinValue (0001-01-01) for an unset date instead of null.
const isUnsetDate = (date) => !date || !date.isValid() || date.year() <= 1;

// Raw subscription assignments carry `startDate` as ISO; the view-models built in Main.js as 'DD.MM.YYYY'.
const parseStartDate = (date) => (date ? moment(date, [moment.ISO_8601, 'DD.MM.YYYY'], true) : null);

// Last day a partner's prepaid plan (#2357) covers. Its renewal and payment dates mean nothing:
// nothing renews or charges it, a new code from the partner's shop buys the next term. So it runs
// one term from its start date - a year for billing "annual", a month for "monthly" - through the
// day before the same date a term later. Takes a raw subscription assignment (the plan in
// `subscriptionType`) or a Main.js view-model (the plan's fields flattened). Null for a plan that
// isn't prepaid, whose dates hellohousing's own billing handles as before, and without a start date.
export const getPrepaidTermEnd = (subscription) => {
  const plan = typeof subscription?.subscriptionType === 'object' && subscription.subscriptionType ? subscription.subscriptionType : subscription;
  if (!isPrepaidSubscription(plan)) {
    return null;
  }
  const start = parseStartDate(subscription?.startDate);
  if (isUnsetDate(start)) {
    return null;
  }
  return start.clone().add(1, getBillingPeriod(plan) === BILLING_PERIOD.ANNUAL ? 'year' : 'month').subtract(1, 'day');
};

// A prepaid plan is over once the last day of its term has passed; the customer then needs a new
// code from the partner's shop.
export const hasPrepaidTermEnded = (subscription, today = moment()) => {
  const termEnd = getPrepaidTermEnd(subscription);
  return termEnd !== null && termEnd.isBefore(today, 'day');
};

// The prepaid plan whose term ran out most recently (`subscriptions`: the account's plans), for the
// renewal hint shown while no plan is running. Null when there's none.
export const getExpiredPrepaidSubscription = (subscriptions, today = moment()) =>
  (subscriptions || [])
    .filter((subscription) => isPrepaidSubscription(subscription) && hasPrepaidTermEnded(subscription, today))
    .sort((a, b) => getPrepaidTermEnd(b).valueOf() - getPrepaidTermEnd(a).valueOf())[0] || null;

// The renewal hint for such a plan, worded as on the subscription page.
export const getExpiredPrepaidText = (subscription) => {
  const partner = getSubscriptionPartner(subscription);
  return `Ihr hellohousing ${getSubscriptionDisplayName(subscription)}-Abonnement${partner ? ` über ${partner.name}` : ''} war bis zum ${getPrepaidTermEnd(subscription).format('DD.MM.YYYY')} gültig. Bitte erneuern Sie Ihr Abonnement mit einem neuen Code${partner ? ` von ${partner.name}` : ''}.`;
};

// A payment mandate hellohousing can charge: Mollie authorized it and no payment has failed since.
export const hasAuthorizedPaymentMethod = (accountData) => accountData?.accountPaymentStatus === 'AUTHORIZED';

// Add-ons beyond what a plan includes (extra properties, storage, AI credits, postage) are charged
// by hellohousing directly, so none is booked without an authorized payment mandate - whatever the
// plan (#2357: a partner's customer has paid the partner and may have no mandate with hellohousing
// at all). Unknown account data counts as no mandate. `currentSubscription` is kept for the callers
// and for the wording (PaymentMethodRequiredModal); the rule itself doesn't depend on the plan.
export const requiresPaymentMethodForAddOns = (currentSubscription, accountData) => !hasAuthorizedPaymentMethod(accountData);

// Storage a Professional plan includes before hellohousing charges for more (the documents page:
// 2 GB, "zusätzlicher Speicherplatz: 1 EUR / Monat je GB"), in MB as the pages read
// documents/status (usedSpaceInBytes / 1000000). Starter and Premium can't buy storage; they are
// sent to the plans instead, as before.
export const PROFESSIONAL_INCLUDED_STORAGE_MB = 2000;

export const requiresPaymentMethodForStorage = (currentSubscription, accountData, usedSpaceInMB) =>
  isProfessionalTier(currentSubscription) &&
  usedSpaceInMB >= PROFESSIONAL_INCLUDED_STORAGE_MB &&
  requiresPaymentMethodForAddOns(currentSubscription, accountData);

export const isAnnualSubscription = (subscriptionType) =>
  getBillingPeriod(subscriptionType) === BILLING_PERIOD.ANNUAL;

// Raw plans from GET /subscriptions carry `price`; the flattened currentSubscription /
// nextSubscription view-models carry the same figure as `subscriptionAmount`.
const getPlanPrice = (subscription) => Number(subscription?.price ?? subscription?.subscriptionAmount) || 0;

// Headline figure shown on plan cards / the confirmation modal: always the monthly-equivalent
// amount, so all tiers stay visually comparable regardless of which billing period is selected.
export const getMonthlyEquivalentPrice = (subscriptionType) =>
  isAnnualSubscription(subscriptionType) ? getPlanPrice(subscriptionType) / 12 : getPlanPrice(subscriptionType);

// The amount actually invoiced per billing cycle (the yearly total, for annual plans).
export const getYearlyPrice = (subscriptionType) =>
  isAnnualSubscription(subscriptionType) ? getPlanPrice(subscriptionType) : getPlanPrice(subscriptionType) * 12;

// Euro and cent parts for the price cards, which render the cents in a smaller font
// ("19" + "50" + "€"). Always two cent digits: splitting `price.toString()` on the decimal point
// rendered whole-euro prices such as an annual plan's 234 as "234undefined0".
export const splitPriceForDisplay = (price) => {
  const [euros, cents] = (Number(price) || 0).toFixed(2).split('.');
  return { euros, cents };
};

/**
 * % an annual plan saves vs. paying its monthly counterpart for 12 months. The backend doesn't
 * return this - it has to be derived on the frontend from the matching monthly plan's price, so
 * the full `subscriptionTypes` list (from GET /subscriptions) is needed to find that counterpart.
 * Returns null when there's no matching monthly plan to compare against, or there's no savings.
 */
export const getAnnualSavingsPercent = (annualSubscriptionType, subscriptionTypes = []) => {
  if (!isAnnualSubscription(annualSubscriptionType)) return null;

  const monthlyCounterpart = subscriptionTypes.find(
    (subscriptionType) =>
      subscriptionType?.name === annualSubscriptionType?.name && !isAnnualSubscription(subscriptionType)
  );
  if (!monthlyCounterpart?.price) return null;

  const monthlyBilledYearly = monthlyCounterpart.price * 12;
  const annualPrice = getYearlyPrice(annualSubscriptionType);
  if (monthlyBilledYearly <= 0) return null;

  const savings = ((monthlyBilledYearly - annualPrice) / monthlyBilledYearly) * 100;
  return savings > 0 ? savings : null;
};

// "20 %": rounded, with a non-breaking space before the sign, as German texts write it, so the
// number and the sign never end up on separate lines.
export const formatSavingsPercent = (percent) => `${Math.round(percent)}\u00A0%`;

// The pieces of "Jährliche Abrechnung: XX,XX € / Jahr - Sie sparen XX % gegenüber monatlicher
// Abrechnung.", so a page can set the savings part in bold (see components/AnnualBillingSummary).
export const getAnnualBillingSummaryParts = (subscriptionType, subscriptionTypes = []) => {
  const savingsPercent = getAnnualSavingsPercent(subscriptionType, subscriptionTypes);
  return {
    yearly: `Jährliche Abrechnung: ${formatNumber(getYearlyPrice(subscriptionType), true)} / Jahr`,
    savings: savingsPercent !== null ? `Sie sparen ${formatSavingsPercent(savingsPercent)}` : null,
  };
};

// "Jährliche Abrechnung: XX,XX € / Jahr - Sie sparen XX % gegenüber monatlicher Abrechnung."
export const getAnnualBillingSummaryText = (subscriptionType, subscriptionTypes = []) => {
  const { yearly, savings } = getAnnualBillingSummaryParts(subscriptionType, subscriptionTypes);
  return savings ? `${yearly} - ${savings} gegenüber monatlicher Abrechnung.` : `${yearly}.`;
};

// Highest savings among the annual plans, rounded like the plan cards ("bis 20 % sparen" on the
// billing period selector). Null when there is no annual plan with a monthly counterpart.
export const getMaxAnnualSavingsPercent = (subscriptionTypes = []) => {
  const savings = subscriptionTypes
    .filter(isAnnualSubscription)
    .map((subscriptionType) => getAnnualSavingsPercent(subscriptionType, subscriptionTypes))
    .filter((percent) => percent !== null);
  return savings.length ? Math.round(Math.max(...savings)) : null;
};

// "Jährliche Abrechnung" / "Monatliche Abrechnung", the line under a plan's price. A prepaid
// partner plan isn't billed by hellohousing at all, so it names the term instead.
export const getBillingPeriodLabel = (subscription) => {
  if (isPrepaidSubscription(subscription)) {
    return `Laufzeit ${getTermLabel(subscription)}, vorausbezahlt`;
  }
  return isAnnualSubscription(subscription) ? 'Jährliche Abrechnung' : 'Monatliche Abrechnung';
};

// "(jährliche Abrechnung)" / "(monatliche Abrechnung)", written after a plan's name in running text.
export const getBillingPeriodSuffix = (subscription) =>
  `(${isAnnualSubscription(subscription) ? 'jährliche' : 'monatliche'} Abrechnung)`;

/**
 * Whether switching from `currentSubscription` to `targetSubscriptionType` is a downgrade,
 * covering the annual-plan cases (backend ticket calls these out explicitly - "no exception"):
 *  - monthly -> annual: always an upgrade, regardless of tier.
 *  - annual -> monthly: always a downgrade, regardless of tier.
 *  - annual -> annual: a downgrade only if moving to a lower tier.
 *  - monthly -> monthly: unchanged, existing tier-based behaviour.
 * Tier comparison goes through `category` (#2470, see helpers/subscriptionFeatureAccess.js)
 * rather than the plan's display `name`, so a custom-named plan still ranks correctly by tier.
 * `currentSubscription`/`targetSubscriptionType` are expected to expose `billing`/
 * `subscriptionBilling`, matching the shapes already used across the app.
 */
export const isSubscriptionSwitchADowngrade = (currentSubscription, targetSubscriptionType) => {
  const currentIsAnnual = isAnnualSubscription(currentSubscription);
  const targetIsAnnual = isAnnualSubscription(targetSubscriptionType);

  if (!currentIsAnnual && targetIsAnnual) return false;
  if (currentIsAnnual && !targetIsAnnual) return true;
  if (currentIsAnnual && targetIsAnnual) {
    return getSubscriptionTierRank(targetSubscriptionType) < getSubscriptionTierRank(currentSubscription);
  }

  // monthly -> monthly: pre-existing tier-based rule.
  const currentTier = getSubscriptionTier(currentSubscription);
  const targetTier = getSubscriptionTier(targetSubscriptionType);
  if (
    currentTier === SUBSCRIPTION_CATEGORY.PROFESSIONAL &&
    currentSubscription?.subscriptionKey !== 'PROFESSIONAL_TESTING_12_2024'
  ) {
    return targetTier === SUBSCRIPTION_CATEGORY.STARTER || targetTier === SUBSCRIPTION_CATEGORY.PREMIUM;
  }
  if (currentTier === SUBSCRIPTION_CATEGORY.PREMIUM) {
    return targetTier === SUBSCRIPTION_CATEGORY.STARTER;
  }
  return false;
};
